import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle, KeyRound, Loader2, RefreshCw, Unlock } from "lucide-react";
import { toast } from "sonner";

type Event = {
  id: string;
  released_count: number;
  ttl_seconds: number;
  released_ids: string[];
  source: string;
  actor_id: string | null;
  created_at: string;
};

const RECENT_WINDOW_MS = 15 * 60 * 1000;

export function HelpdeskAlertLockReleasePanel() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [releasing, setReleasing] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("helpdesk_alert_lock_release_events" as any)
      .select("id, released_count, ttl_seconds, released_ids, source, actor_id, created_at")
      .order("created_at", { ascending: false })
      .limit(50);
    setLoading(false);
    if (error) return toast.error(`Load lock-release events: ${error.message}`);
    setEvents((data ?? []) as unknown as Event[]);
  };

  useEffect(() => { void load(); }, []);

  const releaseNow = async () => {
    setReleasing(true);
    const { data, error } = await supabase.rpc("admin_release_expired_alert_failure_locks" as any, { _ttl_seconds: 300 });
    setReleasing(false);
    if (error) return toast.error(`Release failed: ${error.message}`);
    const released = (data as any)?.released ?? 0;
    toast.success(released > 0 ? `Released ${released} stale lock${released === 1 ? "" : "s"}` : "No expired locks to release");
    void load();
  };

  const recentReleased = events
    .filter((e) => Date.now() - new Date(e.created_at).getTime() < RECENT_WINDOW_MS)
    .reduce((sum, e) => sum + (e.released_count || 0), 0);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="size-4" /> Expired delivery-lock releases
          </CardTitle>
          <CardDescription>
            A scheduled job clears stale locks on failed deliveries every minute (default TTL 5 min) so
            admins are never blocked by an abandoned retry. Use the button for an immediate sweep.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={releaseNow} disabled={releasing}>
            {releasing ? <Loader2 className="size-3.5 mr-1 animate-spin" /> : <Unlock className="size-3.5 mr-1" />}
            Release now
          </Button>
          <Button variant="ghost" size="icon-sm" onClick={() => void load()} aria-label="Refresh">
            {loading ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {recentReleased > 0 && (
          <Alert variant="destructive" className="border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-100">
            <AlertTriangle className="size-4" />
            <AlertTitle>Stale locks detected</AlertTitle>
            <AlertDescription>
              {recentReleased} expired lock{recentReleased === 1 ? "" : "s"} were released in the last 15 minutes.
              An admin session may have crashed mid-retry, or a batch was interrupted without cleaning up.
            </AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="py-4 text-center"><Loader2 className="size-4 animate-spin inline" /></div>
        ) : events.length === 0 ? (
          <div className="py-4 text-center text-xs text-muted-foreground">No release events yet.</div>
        ) : (
          <div className="divide-y divide-border/60 rounded-md border border-border text-xs">
            {events.map((e) => (
              <div key={e.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                <Badge variant={e.released_count > 0 ? "default" : "outline"}>
                  {e.released_count} released
                </Badge>
                <Badge variant="outline">{e.source}</Badge>
                <span className="text-muted-foreground">TTL {e.ttl_seconds}s</span>
                <span className="ml-auto text-muted-foreground">
                  {new Date(e.created_at).toLocaleString()}
                </span>
                {e.released_ids?.length > 0 && (
                  <span className="basis-full font-mono text-[10px] text-muted-foreground truncate" title={e.released_ids.join(", ")}>
                    {e.released_ids.slice(0, 6).map((i) => i.slice(0, 8)).join(", ")}
                    {e.released_ids.length > 6 ? ` +${e.released_ids.length - 6} more` : ""}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default HelpdeskAlertLockReleasePanel;
